import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TimeoffCalculationEntiresReportService } from './services/timeoff-calculation-entires-report.service';
import { TranslateService } from '@ngx-translate/core';
import { SortDescriptor } from '@progress/kendo-data-query';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { EmployeeLeaveService } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-details/components/employee-leave/services/employee-leave.service';
import { EmployeeTimeOffType } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-details/components/employee-leave/models/leave-request.model';
import { api_routes } from '@app/consts';
import { TimeoffCalculationEntriesReportApiResponse } from './models/timeoff-calculation-entries-report.model';

@Component({
  selector: 'app-timeoff-calculation-entries-report',
  templateUrl: './timeoff-calculation-entries-report.component.html',
  styleUrls: ['./timeoff-calculation-entries-report.component.scss']
})
export class TimeoffCalculationEntriesReportComponent implements OnInit {
  employeeId: string;
  timeoffTypeId: string;
  error: boolean;
  isLoading: boolean;
  data: TimeoffCalculationEntriesReportApiResponse[];
  public columns: any[] = [
    { field: "type", title: this.translate.instant("TimeOffCalculationLog-Type"), localizationCode: "TimeOffCalculationLog-Type", type: 'chip', chipValue: ['Accrual', 'Absence', 'Manual Adjustment', 'Carry Over'] },
    { field: "createdOn", title: this.translate.instant("TimeOffCalculationLog-CreatedOn"), localizationCode: "TimeOffCalculationLog-CreatedOn", type: 'dateTime' },
    { field: "on", title: this.translate.instant("TimeOffCalculationLog-On"), localizationCode: "TimeOffCalculationLog-On", type: 'date' },
    { field: "adjustment", title: this.translate.instant("TimeOffCalculationLog-Adjustment"), localizationCode: "TimeOffCalculationLog-Adjustment", type: 'decimal' },
    { field: "runningTotal", title: this.translate.instant("TimeOffCalculationLog-RunningTotal"), localizationCode: "TimeOffCalculationLog-RunningTotal", type: 'decimal' },
    { field: "maximumAccrual", title: this.translate.instant("TimeOffCalculationLog-MaximumAccrual"), localizationCode: "TimeOffCalculationLog-MaximumAccrual", type: 'decimal' },
    { field: "maximumCarryOver", title: this.translate.instant("TimeOffCalculationLog-MaximumCarryOver"), localizationCode: "TimeOffCalculationLog-MaximumCarryOver", type: 'decimal' },
    { field: "reason", title: this.translate.instant("TimeOffCalculationLog-Reason"), localizationCode: "TimeOffCalculationLog-Reason" },
  ];
  public sort: SortDescriptor[] = [{ field: "createdOn", dir: "desc" }];

  form: UntypedFormGroup;
  formElements = [
    {
      formControl: 'employeeId',
      text: 'Employee'
    },
    {
      formControl: 'timeoffTypeId',
      text: 'Time Off Type'
    },
  ]
  timeOffTypeList: EmployeeTimeOffType[];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private translate: TranslateService,
    private timeoffCalculationEntiresReportService: TimeoffCalculationEntiresReportService,
    private employeeLeaveService: EmployeeLeaveService
  ) { }

  ngOnInit(): void {
    this.employeeId = this.getEmployeeIdInURL();
    this.timeoffTypeId = this.getTimeoffTypeIdInURL();

    if (this.employeeId !== null) {
      this.getTimeOffTypes();
    }

    this.form = this.fb.group({
      employeeId: this.employeeId || null,
      timeoffTypeId: this.timeoffTypeId || null,
    });

    this.form.get('employeeId').valueChanges.subscribe((newEmployeeId: string) => {
      if (newEmployeeId !== this.employeeId) {
        this.onEmployeeSelected(newEmployeeId);
      }
    });

    this.form.get('timeoffTypeId').valueChanges.subscribe((newTimeoffTypeId: string) => {
      if (newTimeoffTypeId !== this.timeoffTypeId) {
        this.onTimeoffTypeSelected(newTimeoffTypeId);
      }
    });

    if (this.employeeId && this.timeoffTypeId) {
      this.getReportData();
    } else {
      this.isLoading = false;
      this.error = false;
    }
  }

  getTimeOffTypes() {
    this.employeeLeaveService.getEmployeeTimeOffTypes(this.employeeId)
      .subscribe(
        {
          next: (v) => {
            this.timeOffTypeList = v.filter(item => item.timeOffPolicy !== null);
            if (this.timeoffTypeId) {
              this.form.patchValue({ timeoffTypeId: this.timeoffTypeId });
            }
          },
          error: (e) => {
            this.error = true;
            this.isLoading = false;
          },
          complete: () => this.isLoading = false
        }
      );
  }

  getEmployeeIdInURL(): string {
    let IdInURL: string;

    this.route.paramMap.subscribe(
      params => IdInURL = params.get('employeeId')
    );

    return IdInURL;
  }

  getTimeoffTypeIdInURL(): string {
    let IdInURL: string;

    this.route.paramMap.subscribe(
      params => IdInURL = params.get('timeoffTypeId')
    );

    return IdInURL;
  }

  onEmployeeSelected(employeeId: string): void {
    this.employeeId = employeeId;
    this.timeoffTypeId = null; // Reset timeoffTypeId when employee changes
    this.form.patchValue({ timeoffTypeId: null });

    if (this.employeeId === null || this.employeeId === '') {
      this.router.navigate([`/${api_routes.REPORTS}/${api_routes.PREDEFINED}/${api_routes.TIME_OFF_CALCULATION_ENTRIES}`]);
    }
    else {
      this.router.navigate([`/${api_routes.REPORTS}/${api_routes.PREDEFINED}/${api_routes.TIME_OFF_CALCULATION_ENTRIES}`, this.employeeId]);
    }
  }

  onTimeoffTypeSelected(timeoffTypeId: string): void {
    this.timeoffTypeId = timeoffTypeId;
    this.router.navigate([`/${api_routes.REPORTS}/${api_routes.PREDEFINED}/${api_routes.TIME_OFF_CALCULATION_ENTRIES}`, this.employeeId, timeoffTypeId]);
  }

  onReset(): void {
    this.employeeId = null;
    this.timeoffTypeId = null;
    this.router.navigate(['./'], { relativeTo: this.route });
  }

  getReportData() {
    this.isLoading = false;

    this.timeoffCalculationEntiresReportService.getReportData(this.employeeId, this.timeoffTypeId)
      .subscribe(
        {
          next: (v) => {
            this.data = v;
          },
          error: (e) => {
            this.error = true;
            this.isLoading = false;
          },
          complete: () => this.isLoading = false
        }
      );
  }

}
